import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SwordsIntro — front-door brand gesture shown on every visit to the
 * public landing page (`/`). Two crossed gold swords slide in from
 * off-screen, clash with a spark + flash + held gold glow, the
 * wordmark rises beneath, the stage fades.
 *
 * Owen Feb 2026 — promoted from "once per browser, forever" to "every
 * visit to `/` only." Three deliberate guardrails:
 *   1. Public landing only — never fires on /dashboard or any
 *      authenticated route. Returning members aren't taxed.
 *   2. Tap anywhere to skip — the impatient get out instantly,
 *      the committed get the full cinema.
 *   3. Honours prefers-reduced-motion — accessibility users get no
 *      animation at all.
 *
 * Pure CSS keyframes, no audio. Total runtime ~3.8s; the spark+flash
 * burst lives in the 0.9s → 1.5s window.
 */
const SwordsIntro = ({
  // Owen Feb 2026 — optional props let other surfaces (e.g. the
  // /reading-room landing redirect) reuse the same cinematic with
  // their own gating, without disturbing the default homepage
  // behaviour. All four are optional; defaults preserve the
  // first-visit-only homepage ritual exactly as before.
  forceShow = false,                          // bypass the pathname === '/' check
  gateKey = 'colosseum_intro_seen',           // storage key for once-per-X gating
  gateStorage = 'local',                      // 'local' | 'session' | 'none'
  onComplete = null,                          // fired at the 6.3s mark (or on skip)
} = {}) => {
  const location = useLocation();
  // Lazy initialiser computes the initial show state synchronously so
  // we don't trip react-hooks/set-state-in-effect. The cinematic is
  // gated to the public landing page by default; pass `forceShow` to
  // allow another route (Reading Room redirect) to mount it.
  const [show, setShow] = useState(() => {
    try {
      if (typeof window === 'undefined') return false;
      if (!forceShow && location.pathname !== '/') return false;
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) return false;
      const store = gateStorage === 'session' ? window.sessionStorage
                  : gateStorage === 'local'   ? window.localStorage
                  : null;
      if (store && store.getItem(gateKey)) return false;
      if (store) store.setItem(gateKey, '1');
      return true;
    } catch (e) {
      return false;
    }
  });
  const [phase, setPhase] = useState('in'); // 'in' -> 'spark' -> 'out' -> done

  useEffect(() => {
    if (!show) return;
    // Timeline:
    //   0.0s — both swords begin sliding in
    //   0.9s — swords meet, spark + flash fire
    //   1.5s — flash settles into a steady gold glow
    //   1.1s — wordmark rises beneath the crossed blades
    //   1.5s → 3.0s — the clash is held with glow + wordmark
    //   3.0s — stage begins to fade
    //   3.8s — overlay fully removed
    // Owen Feb 2026 — extended timeline. After the swords clash holds,
    // the swords + wordmark fade out and the place-of-substance phrase
    // reveals with laurel sprigs unfurling outward from the centre.
    //   0.0s → 0.9s    swords slide in
    //   0.9s           clash + spark
    //   1.1s → 1.5s    wordmark rises
    //   1.5s → 2.2s    held clash
    //   2.2s           phase → 'transition' (swords + wordmark fade)
    //   2.7s           phase → 'phrase' (phrase + laurels reveal)
    //   2.7s → 9.0s    phrase held (~6.3 seconds — gives investors
    //                  time to actually READ the catchphrase before
    //                  it disappears). Owen Feb 2026 round 2:
    //                  "needed another three or four seconds."
    //   9.0s           phase → 'out' (everything fades, 1500ms graceful)
    //   10.5s          overlay removed
    const t1 = setTimeout(() => setPhase('spark'), 900);
    const t2 = setTimeout(() => setPhase('transition'), 2200);
    const t3 = setTimeout(() => setPhase('phrase'), 2700);
    const t4 = setTimeout(() => setPhase('out'), 9000);
    const t5 = setTimeout(() => {
      setShow(false);
      // Owen Feb 2026 — fire onComplete at the natural end so the
      // Reading Room redirect (or any other caller) can hand off
      // cleanly to its destination.
      if (typeof onComplete === 'function') onComplete();
    }, 10500);
    return () => { [t1, t2, t3, t4, t5].forEach(clearTimeout); };
  }, [show]);

  // Owen Feb 2026 — listen for a "replay" event so the footer link
  // (or any other surface) can trigger the intro on demand without
  // a route change. Resets phase to 'in' so the full animation
  // sequence runs cleanly from the slide-in.
  useEffect(() => {
    const handler = () => {
      setPhase('in');
      setShow(true);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('replay-swords-intro', handler);
      return () => window.removeEventListener('replay-swords-intro', handler);
    }
  }, []);

  // Tap-anywhere-to-skip handler. Fades the stage immediately rather
  // than just hiding it, so the visual hand-off still feels intentional.
  const skip = () => {
    if (!show) return;
    setPhase('out');
    setTimeout(() => {
      setShow(false);
      if (typeof onComplete === 'function') onComplete();
    }, 1500);
  };

  if (!show) return null;

  return (
    <div
      className={`theme-fixed fixed inset-0 z-[100] flex items-center justify-center bg-[#1C1917] transition-opacity duration-[1500ms] ease-out ${
        phase === 'out' ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ pointerEvents: phase === 'out' ? 'none' : 'auto', cursor: 'pointer' }}
      onClick={skip}
      onTouchStart={skip}
      role="button"
      tabIndex={0}
      aria-label="Skip intro"
      data-testid="swords-intro-overlay"
    >
      {/* Subtle gold radial wash so the stage feels lit, not flat black */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(212,175,55,0.18), rgba(28,25,23,0) 60%)',
        }}
      />

      <style>{`
        @keyframes sword-in-left {
          0%   { transform: translateX(-120vw) rotate(-45deg); opacity: 0; }
          80%  { transform: translateX(0) rotate(-45deg); opacity: 1; }
          100% { transform: translateX(0) rotate(-45deg); opacity: 1; }
        }
        @keyframes sword-in-right {
          0%   { transform: translateX(120vw) rotate(45deg); opacity: 0; }
          80%  { transform: translateX(0) rotate(45deg); opacity: 1; }
          100% { transform: translateX(0) rotate(45deg); opacity: 1; }
        }
        @keyframes spark-ring {
          0%   { transform: scale(0.2); opacity: 0; }
          30%  { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(2.4); opacity: 0; }
        }
        @keyframes spark-flash {
          0%, 100% { opacity: 0; }
          20%, 60% { opacity: 1; }
        }
        @keyframes held-glow {
          /* Gentle, steady gold pulse at the contact point — fades in
             after the initial spark and lives throughout the held
             clash so the centre never looks empty. Owen Jun 2026
             round 34 (extended-hold pass). */
          0%   { opacity: 0; transform: scale(0.6); }
          30%  { opacity: 0.65; transform: scale(1); }
          70%  { opacity: 0.55; transform: scale(1.08); }
          100% { opacity: 0.45; transform: scale(1); }
        }
        @keyframes wordmark-rise {
          0%   { transform: translateY(8px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes phrase-fade {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        /* Single unfurl keyframe — both sprigs animate from a collapsed
           point at the centre nub outward to full width. The RIGHT sprig
           is mirrored at the wrapper level (transform: scaleX(-1)) so a
           single keyframe drives both. Owen Feb 2026 — the previous
           dual-keyframe approach put scaleX(-1) on the animation end-
           state with a left-edge origin, which overlapped the right
           sprig on top of the left at the centre nub. */
        @keyframes laurel-unfurl {
          0%   { transform: scaleX(0); opacity: 0; }
          60%  { opacity: 1; }
          100% { transform: scaleX(1); opacity: 1; }
        }
      `}</style>

      {/* Crossed-swords stage — fades out when transitioning to phrase */}
      <div
        className="relative w-[280px] h-[280px] sm:w-[360px] sm:h-[360px] transition-opacity duration-[500ms] ease-out"
        style={{ opacity: (phase === 'in' || phase === 'spark') ? 1 : 0 }}
      >
        {/* Left-hand sword (slides in from the left, tilts -45°) */}
        <Sword
          style={{
            animation: 'sword-in-left 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards',
            transformOrigin: 'center',
          }}
        />
        {/* Right-hand sword (slides in from the right, tilts +45°) */}
        <Sword
          style={{
            animation: 'sword-in-right 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards',
            transformOrigin: 'center',
          }}
        />

        {/* Spark ring + flash — only fires at the moment of contact */}
        {phase !== 'in' && (
          <>
            <div
              className="absolute inset-0 m-auto rounded-full"
              style={{
                width: '120px',
                height: '120px',
                left: 0, right: 0, top: 0, bottom: 0,
                border: '2px solid rgba(212,175,55,0.9)',
                boxShadow:
                  '0 0 40px rgba(212,175,55,0.65), inset 0 0 30px rgba(212,175,55,0.4)',
                animation: 'spark-ring 800ms ease-out forwards',
              }}
            />
            <div
              className="absolute inset-0 m-auto rounded-full"
              style={{
                width: '60px',
                height: '60px',
                left: 0, right: 0, top: 0, bottom: 0,
                background:
                  'radial-gradient(circle, rgba(255,235,180,0.95), rgba(212,175,55,0.5) 50%, rgba(212,175,55,0) 80%)',
                animation: 'spark-flash 600ms ease-out forwards',
              }}
            />
            {/* Held-glow — gentle gold halo at the contact point that
                lights through the extended hold beat. Without this the
                centre goes dark ~0.8s after the spark and the clash
                feels lifeless. Owen Jun 2026 round 34. */}
            <div
              className="absolute inset-0 m-auto rounded-full"
              style={{
                width: '90px',
                height: '90px',
                left: 0, right: 0, top: 0, bottom: 0,
                background:
                  'radial-gradient(circle, rgba(255,225,150,0.55), rgba(212,175,55,0.18) 55%, rgba(212,175,55,0) 80%)',
                animation: 'held-glow 1800ms ease-in-out 500ms forwards',
              }}
            />
          </>
        )}
      </div>

      {/* Wordmark beneath the swords — fades out with the swords */}
      <div
        className="absolute bottom-[18%] left-0 right-0 text-center transition-opacity duration-[500ms] ease-out"
        style={{
          animation: (phase === 'in' || phase === 'spark') ? 'wordmark-rise 700ms ease-out 700ms both' : 'none',
          opacity: (phase === 'in' || phase === 'spark') ? 1 : 0,
        }}
      >
        <p
          className="text-[10px] sm:text-xs uppercase tracking-[0.5em] text-[#D4AF37]"
          style={{ fontFamily: 'Work Sans, sans-serif' }}
        >
          Welcome to the
        </p>
        <h2
          className="mt-2 text-3xl sm:text-5xl font-light italic text-[#F4EFE6]"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          Colosseum
        </h2>
      </div>

      {/* ─────────────────────────────────────────────────────────
          PHRASE + LAURELS — Owen Feb 2026.
          Appears at phase 'phrase' (2.7s in), holds 2.7s, fades.
          Two laurel sprigs unfurl outward from the centre — left
          sprig translates/rotates LEFT, right sprig MIRRORS right.
          Phrase fades in 200ms before laurels finish unfurling so
          the eye lands on the words first.
          ───────────────────────────────────────────────────────── */}
      <div
        className="absolute left-0 right-0 text-center px-6 transition-opacity duration-[800ms] ease-out"
        style={{
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: phase === 'phrase' ? 1 : 0,
          pointerEvents: phase === 'phrase' ? 'auto' : 'none',
        }}
        data-testid="swords-intro-phrase"
      >
        <p
          className="mx-auto max-w-[760px] text-xl sm:text-3xl italic leading-relaxed text-[#F5EDE0]"
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            animation: phase === 'phrase' ? 'phrase-fade 700ms ease-out 100ms both' : 'none',
          }}
        >
          The only digital place of substance where you can step away from the noise, breathe, pause and contemplate the things that really matter.
        </p>

        {/* Laurel sprigs — unfurl outward from centre. Inline SVGs so
            we control the path stroke colour + animation. */}
        <div className="relative mt-7 h-10 flex items-center justify-center">
          {/* Left sprig — starts collapsed at the centre nub (right
              edge of the SVG, origin 100% 50%) and unfurls leftward. */}
          <svg
            viewBox="0 0 110 30"
            width="120"
            height="34"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              animation: phase === 'phrase' ? 'laurel-unfurl 900ms cubic-bezier(0.22, 1, 0.36, 1) 250ms both' : 'none',
              transformOrigin: '100% 50%',
            }}
            aria-hidden="true"
          >
            {/* Stem */}
            <path d="M 10 15 Q 55 15 105 15" stroke="#D4AF37" strokeWidth="1.2" fill="none" />
            {/* Leaves on top */}
            {[20, 35, 50, 65, 80, 95].map((cx, i) => (
              <ellipse key={`tl-${i}`} cx={cx} cy={8} rx="6" ry="3.2" fill="#D4AF37" opacity={0.85} transform={`rotate(-22 ${cx} 8)`} />
            ))}
            {/* Leaves on bottom */}
            {[27, 42, 57, 72, 87].map((cx, i) => (
              <ellipse key={`bl-${i}`} cx={cx} cy={22} rx="6" ry="3.2" fill="#D4AF37" opacity={0.85} transform={`rotate(22 ${cx} 22)`} />
            ))}
            {/* Centre nub where the two sprigs meet */}
            <circle cx="105" cy="15" r="2.2" fill="#F4EFE6" />
          </svg>
          {/* Right sprig — identical artwork, but wrapped in a
              scaleX(-1) container so it visually mirrors leftward.
              The wrapper handles the mirror; the inner SVG animates
              with the SAME keyframe as the left sprig (origin 100%
              50% within the mirrored space = the centre nub). */}
          <span style={{ display: 'inline-block', transform: 'scaleX(-1)', lineHeight: 0 }}>
            <svg
              viewBox="0 0 110 30"
              width="120"
              height="34"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                animation: phase === 'phrase' ? 'laurel-unfurl 900ms cubic-bezier(0.22, 1, 0.36, 1) 250ms both' : 'none',
                transformOrigin: '100% 50%',
              }}
              aria-hidden="true"
            >
              <path d="M 10 15 Q 55 15 105 15" stroke="#D4AF37" strokeWidth="1.2" fill="none" />
              {[20, 35, 50, 65, 80, 95].map((cx, i) => (
                <ellipse key={`tr-${i}`} cx={cx} cy={8} rx="6" ry="3.2" fill="#D4AF37" opacity={0.85} transform={`rotate(-22 ${cx} 8)`} />
              ))}
              {[27, 42, 57, 72, 87].map((cx, i) => (
                <ellipse key={`br-${i}`} cx={cx} cy={22} rx="6" ry="3.2" fill="#D4AF37" opacity={0.85} transform={`rotate(22 ${cx} 22)`} />
              ))}
              <circle cx="105" cy="15" r="2.2" fill="#F4EFE6" />
            </svg>
          </span>
        </div>
      </div>

      {/* Tap-to-skip hint — fades in only after the held beat starts so
          it doesn't compete with the spark moment. Hidden once the
          phrase phase takes over so it doesn't sit beneath the laurels.
          Owen Feb 2026. */}
      <div
        className="absolute bottom-6 left-0 right-0 text-center pointer-events-none transition-opacity duration-[400ms]"
        style={{
          animation: (phase === 'in' || phase === 'spark') ? 'wordmark-rise 600ms ease-out 1600ms both' : 'none',
          opacity: (phase === 'in' || phase === 'spark') ? undefined : 0,
        }}
      >
        <p
          className="text-[10px] uppercase tracking-[0.3em] text-[#8A7E66] italic"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          tap to enter
        </p>
      </div>
    </div>
  );
};

/**
 * Sword — single SVG sword, gold blade + dark crossguard. Absolute-
 * positioned to the center of the parent stage. Receives an `animation`
 * via the `style` prop so left + right swords share the same shape and
 * differ only in the keyframe.
 */
const Sword = ({ style }) => (
  <svg
    viewBox="0 0 40 220"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute"
    style={{
      width: '40px',
      height: '220px',
      left: '50%',
      top: '50%',
      marginLeft: '-20px',
      marginTop: '-110px',
      filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))',
      ...style,
    }}
  >
    {/* Blade — long tapered diamond */}
    <polygon
      points="20,4 26,30 26,160 20,170 14,160 14,30"
      fill="url(#bladeGrad)"
      stroke="#9B7E2A"
      strokeWidth="0.6"
    />
    {/* Fuller (center groove) */}
    <line
      x1="20"
      y1="30"
      x2="20"
      y2="158"
      stroke="rgba(0,0,0,0.25)"
      strokeWidth="1"
    />
    {/* Crossguard */}
    <rect x="4" y="166" width="32" height="6" fill="#3D2A1E" stroke="#1C1917" strokeWidth="0.5" />
    <rect x="2" y="166" width="2" height="6" fill="#D4AF37" />
    <rect x="36" y="166" width="2" height="6" fill="#D4AF37" />
    {/* Grip */}
    <rect x="17" y="172" width="6" height="34" fill="#3D2A1E" stroke="#1C1917" strokeWidth="0.5" />
    {/* Pommel */}
    <circle cx="20" cy="210" r="6" fill="#D4AF37" stroke="#9B7E2A" strokeWidth="0.6" />
    <defs>
      <linearGradient id="bladeGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%"   stopColor="#F4EFE6" />
        <stop offset="50%"  stopColor="#E8C5A0" />
        <stop offset="100%" stopColor="#9B7E2A" />
      </linearGradient>
    </defs>
  </svg>
);

export default SwordsIntro;
